import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  listPortfolios,
  setBetSelection,
  deletePortfolio,
  exportBackup,
  importBackup,
  previewBackup,
  deleteAllData,
  saveCheckedResult,
} from "../../shared/lib/portfolioStore";
import { loadDataset } from "../../shared/lib/dataLoaders";
import { checkTicketsAgainstDraw } from "../../shared/lib/checkResult";
import { formatHitResult, summarizeCheckedResult } from "../../shared/lib/resultLabels";
import {
  describeBetComparison,
  determineResultAvailability,
  formatTicketGroupBest,
  getCurrentBetTicketNumbers,
  summarizeBetSelectionResult,
} from "../../shared/lib/betSelection";
import { formatDrawNumbers } from "../../shared/utils/numberFormat";
import { SavedPortfolioCard, EmptyState, PortfolioTicketList, ErrorState, BackLink, InfoHelp, BetTicketPicker } from "../../shared/components";
import { strategyRegistry } from "../../shared/lib/strategyRegistry";
import { groupPortfoliosByDay } from "../../shared/lib/portfolioGrouping";
import type { LotteryDataset, Modality, SavedPortfolio } from "../../shared/types";

/** Day-groups rendered by default, and how many more each "Carregar mais" click reveals. */
const INITIAL_DAY_GROUPS = 3;
const DAY_GROUPS_PER_PAGE = 3;

const MODALITY_LABEL: Record<Modality, string> = { lotofacil: "Lotofácil", megasena: "Mega-Sena" };

/** "Apostado"/"Não apostado" badges keyed by original J number; none when no bet declaration exists. */
function buildBetBadges(portfolio: SavedPortfolio): Record<number, { label: string; tone?: "neutral" | "muted" }> | undefined {
  const bet = getCurrentBetTicketNumbers(portfolio);
  if (bet.length === 0) return undefined;
  const set = new Set(bet);
  const badges: Record<number, { label: string; tone?: "neutral" | "muted" }> = {};
  portfolio.tickets.forEach((_, i) => {
    badges[i + 1] = set.has(i + 1) ? { label: "Apostado" } : { label: "Não apostado", tone: "muted" };
  });
  return badges;
}

export function CarteirasPage({ modality }: { modality?: Modality }) {
  const [portfolios, setPortfolios] = useState<SavedPortfolio[]>([]);
  const [filterModality, setFilterModality] = useState<Modality | "all">(modality ?? "all");
  const [filterBet, setFilterBet] = useState<"all" | "yes" | "no">("all");
  const [selected, setSelected] = useState<SavedPortfolio | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [filterContest, setFilterContest] = useState("");
  const [visibleDayGroups, setVisibleDayGroups] = useState(INITIAL_DAY_GROUPS);
  // Datasets per modality present in the list (loaded once, only to show draw dates on cards).
  const [datasets, setDatasets] = useState<Partial<Record<Modality, LotteryDataset>>>({});
  // "Result not published yet" notice, scoped to the open detail view (not the page-level message).
  const [noResultYet, setNoResultYet] = useState(false);
  const [importPreview, setImportPreview] = useState<{ count: number; raw: unknown } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [editingBet, setEditingBet] = useState<{ portfolio: SavedPortfolio; selected: number[] } | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const editorHeadingRef = useRef<HTMLHeadingElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Guards every setState that follows an `await` below against firing after
  // this component has unmounted (e.g. navigating away while a request is in
  // flight) — a no-op for the normal mounted case, never changes behavior.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const filteredPortfolios = useMemo(() => {
    const q = filterContest.trim();
    if (!q) return portfolios;
    return portfolios.filter((p) => p.contest !== undefined && String(p.contest).includes(q));
  }, [portfolios, filterContest]);
  const dayGroups = useMemo(() => groupPortfoliosByDay(filteredPortfolios), [filteredPortfolios]);

  async function refresh() {
    const filters: Parameters<typeof listPortfolios>[0] = {};
    if (filterModality !== "all") filters.modality = filterModality;
    if (filterBet !== "all") filters.markedAsBet = filterBet === "yes";
    const result = await listPortfolios(filters);
    if (!isMountedRef.current) return;
    setPortfolios(result);
  }

  const modalitiesInList = useMemo(() => Array.from(new Set(portfolios.map((p) => p.modality))).sort().join(","), [portfolios]);
  useEffect(() => {
    let cancelled = false;
    for (const m of modalitiesInList.split(",").filter(Boolean) as Modality[]) {
      loadDataset(m)
        .then((d) => {
          if (!cancelled) setDatasets((prev) => (prev[m] === d ? prev : { ...prev, [m]: d }));
        })
        .catch(() => undefined); // draw-date line is optional; the list works without it
    }
    return () => {
      cancelled = true;
    };
  }, [modalitiesInList]);

  useEffect(() => {
    setNoResultYet(false);
  }, [selected?.id]);

  // When the bet editor opens (or moves to another card), bring it into view only if needed and move focus to its
  // heading so keyboard and screen-reader users land on it. `scroll-mt-16` on the editor keeps it clear of the
  // sticky day-group heading. Keyed by portfolio id so edits inside the editor never re-scroll.
  const editingBetId = editingBet?.portfolio.id;
  useEffect(() => {
    if (!editingBetId) return;
    editorRef.current?.scrollIntoView?.({ block: "nearest" });
    editorHeadingRef.current?.focus({ preventScroll: true });
  }, [editingBetId]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModality, filterBet]);

  async function handleExport() {
    const backup = await exportBackup();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `loterias-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);
    file
      .text()
      .then((text) => {
        const raw = JSON.parse(text);
        const preview = previewBackup(raw);
        if (!preview.valid) {
          setImportError(preview.error);
          return;
        }
        setImportPreview({ count: preview.count, raw });
      })
      .catch((err) => setImportError(String(err)));
  }

  async function confirmImport() {
    if (!importPreview) return;
    const result = await importBackup(importPreview.raw);
    if (!isMountedRef.current) return;
    setMessage(`Importado: ${result.imported} de ${result.totalInFile} (duplicados ignorados: ${result.skippedDuplicates}).`);
    setImportPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    refresh();
  }

  /** Appends a bet-selection revision, stamping whether the result was already in the LotoAtlas dataset (descriptive metadata, not proof of real bet time). */
  async function recordBet(portfolio: SavedPortfolio, selectedTicketNumbers: number[]) {
    let availability: ReturnType<typeof determineResultAvailability> = "unknown";
    let latest: number | undefined;
    try {
      const dataset = await loadDataset(portfolio.modality);
      availability = determineResultAvailability(portfolio.contest, dataset);
      latest = dataset.latestContest;
    } catch {
      // dataset unavailable: recorded as "unknown"
    }
    await setBetSelection(portfolio.id, selectedTicketNumbers, { resultAvailability: availability, datasetLatestContestAtRecording: latest });
    await refresh();
    const fresh = (await listPortfolios()).find((x) => x.id === portfolio.id);
    if (!isMountedRef.current) return;
    setSelected((prev) => (prev && prev.id === portfolio.id && fresh ? fresh : prev));
  }

  async function handleCheck(portfolio: SavedPortfolio) {
    if (!portfolio.contest) return;
    setMessage(null);
    setNoResultYet(false);
    const dataset = await loadDataset(portfolio.modality);
    if (!isMountedRef.current) return;
    const draw = dataset.draws.find((d) => d.contest === portfolio.contest);
    if (!draw) {
      // Shown inside the open detail dialog, next to the button that was clicked.
      setNoResultYet(true);
      return;
    }
    const checkedResult = checkTicketsAgainstDraw(portfolio.tickets, draw);
    await saveCheckedResult(portfolio.id, checkedResult);
    await refresh();
    if (!isMountedRef.current) return;
    // Keep the open detail view in sync immediately, instead of waiting for
    // a later re-open — `selected` is a snapshot, not a live reference into `portfolios`.
    setSelected((prev) => (prev && prev.id === portfolio.id ? { ...prev, checkedResult } : prev));
  }

  return (
    <div className="space-y-6">
      <BackLink to={modality ? `/${modality}/gerar` : "/"} label={modality ? `Voltar para ${MODALITY_LABEL[modality]}` : "Voltar ao início"} />
      <h1 className="text-2xl font-bold">Meus jogos salvos</h1>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">
          Modalidade:{" "}
          <select value={filterModality} onChange={(e) => setFilterModality(e.target.value as Modality | "all")} className="rounded border border-brand-border px-2 py-1">
            <option value="all">Todas</option>
            <option value="lotofacil">Lotofácil</option>
            <option value="megasena">Mega-Sena</option>
          </select>
        </label>
        <label className="text-sm">
          Concurso:{" "}
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={filterContest}
            onChange={(e) => {
              setFilterContest(e.target.value);
              setVisibleDayGroups(INITIAL_DAY_GROUPS);
            }}
            placeholder="nº"
            className="w-24 rounded border border-brand-border bg-transparent px-2 py-1"
          />
        </label>
        <label className="text-sm">
          Com aposta registrada:{" "}
          <select value={filterBet} onChange={(e) => setFilterBet(e.target.value as "all" | "yes" | "no")} className="rounded border border-brand-border px-2 py-1">
            <option value="all">Todas</option>
            <option value="yes">Sim</option>
            <option value="no">Não</option>
          </select>
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={handleExport} className="rounded border border-brand-border px-3 py-1.5 text-sm">
            Exportar backup
          </button>
          <label className="cursor-pointer rounded border border-brand-border px-3 py-1.5 text-sm">
            Importar backup
            <input ref={fileInputRef} type="file" accept="application/json" onChange={handleFileSelected} className="hidden" />
          </label>
          <button
            type="button"
            onClick={() => {
              if (confirm("Apagar TODOS os dados locais? Esta ação não pode ser desfeita.")) {
                deleteAllData().then(refresh);
              }
            }}
            className="rounded border border-rose-700 px-3 py-1.5 text-sm text-rose-300"
          >
            Apagar tudo
          </button>
        </div>
      </div>

      <p className="flex items-center gap-1 text-xs text-brand-textMuted">
        Seus jogos ficam salvos somente neste navegador.
        <InfoHelp
          title="Onde seus jogos ficam salvos"
          body="Não há conta nem sincronização em nuvem: os jogos salvos existem apenas neste navegador, neste dispositivo. Outro dispositivo ou perfil de navegador não os enxerga automaticamente, e limpar os dados do site pode apagá-los. Use “Exportar backup” antes de limpar dados ou trocar de dispositivo se quiser preservá-los."
        />
      </p>

      {importError && <ErrorState title="Backup inválido" message={importError} />}
      {importPreview && (
        <div className="rounded-lg border border-amber-700 bg-amber-950/40 p-3">
          <p className="text-sm">Este arquivo contém {importPreview.count} conjunto(s) de jogos. Confirmar importação?</p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={confirmImport} className="rounded bg-brand-action px-3 py-1.5 text-sm text-brand-actionForeground">
              Confirmar
            </button>
            <button type="button" onClick={() => setImportPreview(null)} className="rounded border border-brand-border px-3 py-1.5 text-sm">
              Cancelar
            </button>
          </div>
        </div>
      )}
      {message && <p role="status" className="text-sm text-brand-textMuted">{message}</p>}

      {portfolios.length === 0 ? (
        <EmptyState title="Nenhum jogo salvo ainda" description="Gere um conjunto de jogos e salve para vê-lo aqui." />
      ) : filteredPortfolios.length === 0 ? (
        <EmptyState title="Nenhum jogo encontrado" description="Nenhum jogo salvo corresponde ao concurso informado." />
      ) : (
        <div className="space-y-6">
          {dayGroups.slice(0, visibleDayGroups).map((group) => (
            <section key={group.dayKey} aria-label={group.label} className="space-y-3">
              <h2 className="sticky top-0 z-10 border-b border-brand-border bg-brand-bg/95 py-1 text-sm font-semibold text-brand-textMuted">{group.label}</h2>
              <div className="grid gap-3 md:grid-cols-2">
                {group.portfolios.map((p) => (
                  <Fragment key={p.id}>
                  <SavedPortfolioCard
                    portfolio={p}
                    dataset={datasets[p.modality]}
                    onOpen={() => setSelected(p)}
                    onEditBet={() => {
                      const current = getCurrentBetTicketNumbers(p);
                      setEditingBet({ portfolio: p, selected: current.length > 0 ? current : p.tickets.map((_, i) => i + 1) });
                    }}
                    onRemoveBet={() => {
                      if (confirm("Remover o registro de aposta? O histórico anterior é preservado e todos os jogos continuam salvos.")) recordBet(p, []);
                    }}
                    onDelete={() => {
                      if (confirm("Excluir este conjunto de jogos?")) deletePortfolio(p.id).then(refresh);
                    }}
                  />
                  {/* Bet editor opens right below the card whose button was clicked, spanning the full grid row
                      (same pattern as the details panel below). It sits before the details when both are open. */}
                  {editingBet && editingBet.portfolio.id === p.id && (
                    <div ref={editorRef} className="scroll-mt-16 md:col-span-2">
                      <div role="dialog" aria-modal="true" aria-label="Registrar jogos apostados" className="space-y-3 rounded-xl border border-brand-border bg-brand-surface p-4">
                        <h2 ref={editorHeadingRef} tabIndex={-1} className="font-semibold outline-none">Jogos apostados</h2>
                        <p className="text-sm text-brand-textMuted">
                          A carteira gerada continua completa ({editingBet.portfolio.tickets.length} jogos). Aqui você só registra quais deles foram realmente apostados.
                        </p>
                        <BetTicketPicker
                          tickets={editingBet.portfolio.tickets}
                          selected={editingBet.selected}
                          onChange={(next) => setEditingBet((prev) => (prev ? { ...prev, selected: next } : prev))}
                        />
                        {editingBet.selected.length === 0 && (
                          <p className="text-xs text-amber-300" role="alert">
                            Selecione ao menos um jogo apostado ou use "Remover registro de aposta".
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const { portfolio, selected: chosen } = editingBet;
                              if (chosen.length === 0) return;
                              recordBet(portfolio, chosen).then(() => setEditingBet(null));
                            }}
                            disabled={editingBet.selected.length === 0}
                            className="rounded bg-brand-action px-3 py-1.5 text-sm text-brand-actionForeground disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Salvar registro
                          </button>
                          <button type="button" onClick={() => setEditingBet(null)} className="rounded border border-brand-border px-3 py-1.5 text-sm">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Details open right below the card that was opened, spanning the full grid row. */}
                  {selected && selected.id === p.id && (
                    <div key={`${p.id}-detail`} className="md:col-span-2">
              <div role="dialog" aria-modal="true" aria-label="Detalhes dos jogos salvos" className="rounded-xl border border-brand-border bg-brand-surface p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-semibold">Detalhes: {strategyRegistry.get(selected.strategyId)?.ux.title ?? selected.strategyId}</h2>
                  <button type="button" onClick={() => setSelected(null)} className="rounded border border-brand-border px-2 py-1 text-sm">
                    Fechar
                  </button>
                </div>
                {(() => {
                  const checkedResult = selected.checkedResult;
                  if (!checkedResult) {
                    return (
                      <>
                        {noResultYet && (
                          <div role="alert" data-testid="no-result-yet" className="mb-3 rounded-lg border border-amber-700 bg-amber-950/40 p-3 text-sm text-amber-100">
                            O resultado oficial do concurso {selected.contest} ainda não foi divulgado pela CAIXA. Volte a conferir mais tarde.
                          </div>
                        )}
                        <PortfolioTicketList tickets={selected.tickets} ticketBadges={buildBetBadges(selected)} />
                        <button type="button" onClick={() => handleCheck(selected)} className="mt-3 rounded border border-brand-border px-3 py-1.5 text-sm">
                          Conferir resultado
                        </button>
                      </>
                    );
                  }
                  const summary = summarizeCheckedResult(selected.modality, checkedResult);
                  const betSummary = summarizeBetSelectionResult(selected.modality, checkedResult.hitsPerTicket, getCurrentBetTicketNumbers(selected));
                  const comparison = describeBetComparison(betSummary);
                  return (
                    <>
                      <div className="mb-3 space-y-2 rounded-lg border border-brand-border bg-brand-surfaceElevated p-3 text-sm">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-brand-textMuted">Resultado oficial, concurso {checkedResult.contest}</p>
                          <p className="mt-0.5 font-mono text-base text-brand-text">{formatDrawNumbers(checkedResult.numbers)}</p>
                        </div>
                        {summary.sentence && (
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-brand-textMuted">{betSummary.hasSelection ? "Melhor da carteira gerada" : "Melhor resultado"}</p>
                            <p className="mt-0.5 text-brand-text">{summary.sentence}</p>
                            {betSummary.overallBestWasNotBet && <p className="mt-0.5 text-xs text-brand-textMuted">O melhor jogo da carteira não foi marcado como apostado.</p>}
                          </div>
                        )}
                        {betSummary.hasSelection && (
                          <div data-testid="bet-comparison" className="space-y-1">
                            {betSummary.bestBet && (
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-brand-textMuted">Melhor entre os apostados</p>
                                <p className="mt-0.5 text-brand-text">{formatTicketGroupBest(betSummary.bestBet)}</p>
                              </div>
                            )}
                            {betSummary.bestNonBet && (
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-brand-textMuted">Melhor entre os não apostados</p>
                                <p className="mt-0.5 text-brand-text">{formatTicketGroupBest(betSummary.bestNonBet)}</p>
                              </div>
                            )}
                            {comparison && <p className="text-sm text-brand-text">{comparison}</p>}
                          </div>
                        )}
                        <p className="text-xs text-brand-textMuted">Conferido em {new Date(checkedResult.checkedAt).toLocaleString("pt-BR")}</p>
                      </div>
                      <PortfolioTicketList
                        tickets={selected.tickets}
                        highlightNumbers={checkedResult.numbers}
                        hitsPerTicket={checkedResult.hitsPerTicket}
                        formatHits={(hits) => formatHitResult(selected.modality, hits)}
                        bestTicketNumbers={summary.bestTicketNumbers}
                        ticketBadges={buildBetBadges(selected)}
                      />
                    </>
                  );
                })()}
              </div>
                    </div>
                  )}
                  </Fragment>
                ))}
              </div>
            </section>
          ))}
          {dayGroups.length > visibleDayGroups && (
            <button
              type="button"
              onClick={() => setVisibleDayGroups((n) => n + DAY_GROUPS_PER_PAGE)}
              className="rounded border border-brand-border px-4 py-2 text-sm hover:bg-white/5"
            >
              Carregar mais
            </button>
          )}
        </div>
      )}
    </div>
  );
}
